// Tier 3 external ref-based LRU cache for ESA message content (DR-1, DR-17).
// NOT a Zustand store. Exposes useSyncExternalStore-compatible subscribe/getSnapshot.
// Snapshot immutability (DR-17): getSnapshot returns a stable array reference that
// only changes when the thread's content changes.

export interface ESACacheMessage {
  id: string;
  conversationId: string;
  parentMessageId: string | null;
  initiator: 'user' | 'system';
  messageType: 'text' | 'json' | 'mix';
  message: Record<string, unknown>;
  /** Converse response metadata (e.g. task_id on task_dispatched). */
  metadata?: Record<string, unknown>;
  hasAttachments: boolean;
  storedFileUkey: string | null;
  createdAt: string;
}

interface ThreadEntry {
  messages: ESACacheMessage[];
  lastAccessedAt: number;
}

/** Module-level stable empty array — returned for unknown threads without allocation. */
const EMPTY_ARRAY: ESACacheMessage[] = [];
Object.freeze(EMPTY_ARRAY);

interface MessageCacheOptions {
  maxThreads?: number;
  maxMessagesPerThread?: number;
}

export class MessageCache {
  private readonly maxThreads: number;
  private readonly maxMessagesPerThread: number;

  /** Internal storage: threadId → entry */
  private threads = new Map<string, ThreadEntry>();

  /** Stable snapshot references per thread (DR-17). */
  private snapshots = new Map<string, ESACacheMessage[]>();

  /** Currently streaming threadId (Track C). */
  private streamingThreadId: string | null = null;

  /** Set of subscriber listeners (useSyncExternalStore). */
  private listeners = new Set<() => void>();

  /** Pinned thread that must never be evicted (active conversation). */
  private pinnedThreadId: string | null = null;

  /** Threads whose messages were loaded from the server (setThread), not SSE-only seeds. */
  private historyLoadedThreads = new Set<string>();

  constructor(options: MessageCacheOptions = {}) {
    this.maxThreads = options.maxThreads ?? 20;
    this.maxMessagesPerThread = options.maxMessagesPerThread ?? 200;
  }

  // ─── Write API ────────────────────────────────────────────────────────────

  /** Append a completed message (Phase 1 blocking response or initial history item). */
  addMessage(threadId: string, message: ESACacheMessage): void {
    this.touchThread(threadId);
    const entry = this.threads.get(threadId)!;
    entry.messages = [...entry.messages, message];
    this.enforceMessageLimit(threadId);
    this.rebuildSnapshot(threadId);
    this.notify();
  }

  /** Prepend older messages loaded via reverse-infinite-scroll pagination.
   *  Deduplicates by message id so repeated scroll-up fetches never double the list. */
  prependHistory(threadId: string, olderMessages: ESACacheMessage[]): void {
    if (olderMessages.length === 0) return;
    this.touchThread(threadId);
    const entry = this.threads.get(threadId)!;
    const existingIds = new Set(entry.messages.map(m => m.id));
    const deduped = olderMessages.filter(m => !existingIds.has(m.id));
    if (deduped.length === 0) return; // nothing new — skip notify
    entry.messages = [...deduped, ...entry.messages];
    this.enforceMessageLimit(threadId);
    this.rebuildSnapshot(threadId);
    this.notify();
  }

  /** Replace all messages for a thread (initial load from server). */
  setThread(
    threadId: string,
    messages: ESACacheMessage[],
    opts?: { markHistoryLoaded?: boolean }
  ): void {
    this.touchThread(threadId);
    const entry = this.threads.get(threadId)!;
    entry.messages = [...messages];
    // Default true for server history loads. Optimistic rekeys should pass false
    // so ESAChatView still fetches authoritative history.
    if (opts?.markHistoryLoaded !== false) {
      this.historyLoadedThreads.add(threadId);
    }
    this.enforceMessageLimit(threadId);
    this.rebuildSnapshot(threadId);
    this.notify();
  }

  /** True after a server history fetch (setThread); false for SSE-only cache seeds. */
  isHistoryLoaded(threadId: string): boolean {
    return this.historyLoadedThreads.has(threadId);
  }

  /** Append a streaming SSE token to the active streaming buffer (Track C). */
  appendToken(threadId: string, token: string): void {
    this.streamingThreadId = threadId;
    this.touchThread(threadId);
    const entry = this.threads.get(threadId)!;
    // Streaming buffer is held in a special sentinel message at the tail
    const last = entry.messages[entry.messages.length - 1];
    if (last && last.id === `__streaming_${threadId}`) {
      const text = (last.message.text as string) + token;
      entry.messages = [
        ...entry.messages.slice(0, -1),
        { ...last, message: { ...last.message, text } },
      ];
    } else {
      entry.messages = [
        ...entry.messages,
        {
          id: `__streaming_${threadId}`,
          conversationId: threadId,
          parentMessageId: null,
          initiator: 'system',
          messageType: 'text',
          message: { text: token },
          hasAttachments: false,
          storedFileUkey: null,
          createdAt: new Date().toISOString(),
        },
      ];
    }
    this.rebuildSnapshot(threadId);
    this.notify();
  }

  /** Finalize streaming — promote buffer to a real message (Track C). */
  flush(): void {
    if (this.streamingThreadId === null) return;
    this.streamingThreadId = null;
    this.notify();
  }

  /** Remove a thread from cache entirely. */
  evict(threadId: string): void {
    this.threads.delete(threadId);
    this.snapshots.delete(threadId);
    this.historyLoadedThreads.delete(threadId);
    if (this.streamingThreadId === threadId) {
      this.streamingThreadId = null;
    }
    this.notify();
  }

  /** Clear entire cache. */
  clear(): void {
    this.threads.clear();
    this.snapshots.clear();
    this.historyLoadedThreads.clear();
    this.streamingThreadId = null;
    this.notify();
  }

  /**
   * Pin a thread so it is never evicted by the LRU policy.
   * Call with the current activeThreadId whenever it changes.
   */
  setPinnedThread(threadId: string | null): void {
    this.pinnedThreadId = threadId;
  }

  // ─── Read API (useSyncExternalStore compatible) ────────────────────────────

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /**
   * Returns a stable array reference for the thread (DR-17).
   * The reference only changes after write mutations on that thread.
   */
  getSnapshot(threadId: string): ESACacheMessage[] {
    const snap = this.snapshots.get(threadId);
    return snap ?? EMPTY_ARRAY;
  }

  // ─── Diagnostics ──────────────────────────────────────────────────────────

  getMemoryUsage(): {
    threadCount: number;
    totalMessages: number;
    isStreaming: boolean;
    streamingThreadId: string | null;
  } {
    let total = 0;
    for (const entry of this.threads.values()) {
      total += entry.messages.length;
    }
    return {
      threadCount: this.threads.size,
      totalMessages: total,
      isStreaming: this.streamingThreadId !== null,
      streamingThreadId: this.streamingThreadId,
    };
  }

  // ─── Private helpers ──────────────────────────────────────────────────────

  private touchThread(threadId: string): void {
    if (!this.threads.has(threadId)) {
      if (this.threads.size >= this.maxThreads) {
        this.evictLRU();
      }
      this.threads.set(threadId, { messages: [], lastAccessedAt: Date.now() });
    } else {
      this.threads.get(threadId)!.lastAccessedAt = Date.now();
    }
  }

  private evictLRU(): void {
    let oldestKey: string | null = null;
    let oldestTs = Infinity;
    for (const [key, entry] of this.threads) {
      if (key === this.pinnedThreadId) continue;
      if (entry.lastAccessedAt < oldestTs) {
        oldestTs = entry.lastAccessedAt;
        oldestKey = key;
      }
    }
    if (oldestKey) {
      this.threads.delete(oldestKey);
      this.snapshots.delete(oldestKey);
      this.historyLoadedThreads.delete(oldestKey);
    }
  }

  private enforceMessageLimit(threadId: string): void {
    const entry = this.threads.get(threadId);
    if (!entry) return;
    if (entry.messages.length > this.maxMessagesPerThread) {
      entry.messages = entry.messages.slice(entry.messages.length - this.maxMessagesPerThread);
    }
  }

  private rebuildSnapshot(threadId: string): void {
    const entry = this.threads.get(threadId);
    if (!entry) {
      this.snapshots.delete(threadId);
      return;
    }
    // Freeze the new snapshot array to enforce immutability externally
    this.snapshots.set(threadId, entry.messages);
  }

  private notify(): void {
    for (const listener of this.listeners) {
      listener();
    }
  }
}
